<template>
	<!-- top e left: spazio vero di barra dell'app e menu laterale (il menu si allarga e si stringe), non numeri fissi -->
	<div class="ai-builder" :style="{ top: ($vuetify.application.top || 64) + 'px', left: $vuetify.application.left + 'px' }">
		<!-- Toolbar -->
		<v-toolbar dense flat class="ai-builder__toolbar" elevation="1">
			<v-btn icon @click="goBack">
				<v-icon>mdi-arrow-left</v-icon>
			</v-btn>
			<v-toolbar-title class="body-2 font-weight-medium">
				{{ templateName || 'AI Email Builder' }}
			</v-toolbar-title>

			<!-- Brand della mail: l'assistente ne usa logo, colori, font, contatti e tono.
			     Nella barra niente v-if, solo v-show: Vuetify sposta gli attivatori dei tooltip fuori
			     dal loro contenitore, e se la fila di elementi cambia forma Vue riordina i nodi
			     sbagliando posto (il selettore spariva, annulla/ripristina finivano dopo Salva) -->
			<div v-show="brands.length" class="ai-builder__brand ml-4">
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<div v-on="on">
							<v-select
								v-model="brandId"
								:items="brands"
								:menu-props="{'offset-y': true}"
								item-text="name"
								item-value="id"
								dense
								outlined
								hide-details
								prepend-inner-icon="mdi-tag-outline"
								class="body-2"
								@change="isDirty = true">
								<template v-slot:item="{ item }">
									<span class="ai-builder__brand-swatch" :style="{ background: item.primary_color || '#e0e0e0' }"></span>
									<span>{{ item.name }}</span>
									<span v-if="item.is_default" class="caption grey--text ml-2">predefinito</span>
								</template>
							</v-select>
						</div>
					</template>
					<span>Brand della mail: l'assistente ne usa logo, colori, font, contatti e tono</span>
				</v-tooltip>
			</div>

			<v-spacer></v-spacer>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon small v-on="on" :disabled="!canUndo" @click="undo">
						<v-icon small>mdi-undo</v-icon>
					</v-btn>
				</template>
				<span>Annulla</span>
			</v-tooltip>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon small v-on="on" :disabled="!canRedo" @click="redo">
						<v-icon small>mdi-redo</v-icon>
					</v-btn>
				</template>
				<span>Ripristina</span>
			</v-tooltip>

			<v-divider vertical class="mx-2"></v-divider>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon small v-on="on" :color="showBorders ? 'primary' : ''" @click="showBorders = !showBorders">
						<v-icon small>mdi-border-all-variant</v-icon>
					</v-btn>
				</template>
				<span>{{ showBorders ? 'Nascondi bordi' : 'Mostra bordi' }}</span>
			</v-tooltip>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon small v-on="on" @click="previewDialog = true" :disabled="!blocks.length">
						<v-icon small>mdi-eye-outline</v-icon>
					</v-btn>
				</template>
				<span>Anteprima</span>
			</v-tooltip>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon small v-on="on" @click="openExportDialog" :disabled="!blocks.length">
						<v-icon small>mdi-code-tags</v-icon>
					</v-btn>
				</template>
				<span>Esporta HTML</span>
			</v-tooltip>

			<v-divider vertical class="mx-2"></v-divider>

			<v-tooltip bottom>
				<template v-slot:activator="{ on }">
					<div v-on="on" class="d-flex align-center">
						<v-icon small class="mr-1">mdi-magnify</v-icon>
						<v-select
							v-model="zoom"
							:items="zoomLevels"
							dense
							hide-details
							class="ai-builder__zoom body-2"
							style="max-width:78px"
							@change="onZoomChange">
						</v-select>
					</div>
				</template>
				<span>Ingrandimento dell'anteprima nell'editor</span>
			</v-tooltip>

			<v-btn small text color="primary" :loading="saving" @click="save" class="ml-2">
				<v-icon small left>mdi-content-save</v-icon>
				Salva
			</v-btn>

			<v-chip v-show="isDirty" x-small color="warning" text-color="white" class="ml-2">
				Non salvato
			</v-chip>

			<!-- Per una campagna vera il footer deve avere tutti i dati legali: si avvisa, non si blocca -->
			<span v-show="footerGaps.length" class="ml-2">
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-chip v-on="on" x-small outlined color="error" @click="selectFooter">
							<v-icon x-small left>mdi-scale-balance</v-icon>Footer incompleto
						</v-chip>
					</template>
					<span>Manca: {{ footerGaps.join(', ') }}</span>
				</v-tooltip>
			</span>
		</v-toolbar>

		<!-- Main content -->
		<div class="ai-builder__content">
			<!-- Left: Blocks sidebar -->
			<div class="ai-builder__sidebar-left">
				<blocks-sidebar
				@add="addBlockFromPalette"
				@model="askModel"></blocks-sidebar>
			</div>

			<!-- Center: Canvas -->
			<editor-canvas
				:blocks="blocks"
				:selected-block-id="selectedBlockId"
				:show-borders="showBorders"
				:zoom="zoom"
				:template-variables="templateVariables"
				@select="selectBlock"
				@deselect="selectedBlockId = null"
				@change="onBlocksChange"
				@move-up="moveBlockUp"
				@move-down="moveBlockDown"
				@duplicate="duplicateBlock"
				@delete="deleteBlock"
				@ai="openAiForBlock"
				@content-change="onContentChange"
				@settings-change="onSettingsChange"
				@child-change="onBlocksChange"
				@model="askModel">
			</editor-canvas>

			<!-- Right: Properties | AI chat -->
			<div class="ai-builder__sidebar-right">
				<div class="ai-builder__tabs">
					<button type="button" class="ai-builder__tab" :class="{ 'ai-builder__tab--active': rightTab === 'props' }" @click="rightTab = 'props'">
						<v-icon small class="mr-1">mdi-tune-variant</v-icon>Proprietà
					</button>
					<button type="button" class="ai-builder__tab" :class="{ 'ai-builder__tab--active': rightTab === 'chat' }" @click="rightTab = 'chat'">
						<v-icon small class="mr-1">mdi-robot-outline</v-icon>Assistente AI
					</button>
				</div>
				<div class="ai-builder__sidebar-body">
					<properties-panel
						v-show="rightTab === 'props'"
						:block="selectedBlock"
						@style-change="onStyleChange"
						@title-style-change="onTitleStyleChange"
						@settings-change="onSettingsChange"
						@content-change="onContentChange">
					</properties-panel>
					<ai-chat
						ref="aiChat"
						v-show="rightTab === 'chat'"
						:messages="aiMessages"
						:suggestions="aiSuggestions"
						:busy="aiBusy"
						:phase="aiPhase"
						:error="aiError"
						:selected-label="selectedBlockLabel"
						:can-undo="canUndo"
						@send="sendAiMessage"
						@undo="undo"
						@clear-chat="clearAiChat"
						@clear-error="aiError = null"
						@deselect="selectedBlockId = null">
					</ai-chat>
				</div>
			</div>
		</div>

		<!-- Preview dialog -->
		<!-- eager: senza, al primo apri l'iframe non e' ancora montato e l'anteprima resta vuota -->
		<v-dialog v-model="previewDialog" max-width="760" scrollable eager>
			<v-card>
				<v-card-title class="pa-3">
					<v-icon class="mr-2">mdi-eye-outline</v-icon>
					<span class="body-1">Anteprima</span>
					<v-spacer></v-spacer>
					<v-btn-toggle v-model="previewMode" dense mandatory class="mr-3">
						<v-btn small value="desktop"><v-icon small left>mdi-monitor</v-icon>Desktop</v-btn>
						<v-btn small value="mobile"><v-icon small left>mdi-cellphone</v-icon>Mobile</v-btn>
					</v-btn-toggle>
					<v-btn icon small @click="previewDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-0 ai-builder__preview-stage" style="height:80vh">
					<iframe ref="previewFrame" class="ai-builder__preview-frame" :style="{ width: previewWidth + 'px' }" frameborder="0" sandbox="allow-same-origin"></iframe>
				</v-card-text>
				<v-divider></v-divider>
				<p class="caption grey--text pa-3 mb-0">
					Le variabili sono riempite con un destinatario di esempio, solo qui: nella mail vera ci vanno i dati del cliente. Resa del browser, molto vicina a Gmail e Apple Mail. Outlook per Windows usa il motore di Word e può differire: per esserne certi invia una mail di prova a una casella Outlook.
				</p>
			</v-card>
		</v-dialog>

		<!-- Export HTML dialog -->
		<v-dialog v-model="exportDialog" max-width="800" scrollable>
			<v-card>
				<v-card-title class="pa-3">
					<v-icon class="mr-2">mdi-code-tags</v-icon>
					<span class="body-1">Esporta</span>
					<v-spacer></v-spacer>
					<v-btn-toggle v-model="exportMode" dense mandatory class="mr-3">
						<v-btn small value="html">HTML</v-btn>
						<v-btn small value="json">JSON blocchi</v-btn>
					</v-btn-toggle>
					<v-btn small text color="primary" @click="copyExportHtml" class="mr-2">
						<v-icon small left>mdi-content-copy</v-icon>
						Copia
					</v-btn>
					<v-btn icon small @click="exportDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-0">
					<textarea ref="exportTextarea" :value="exportText" readonly
						class="ai-builder__export-code"></textarea>
				</v-card-text>
				<v-divider></v-divider>
				<p class="caption grey--text pa-3 mb-0">
					HTML: il codice della mail come viene inviato. JSON blocchi: la struttura che vede e produce l'assistente AI, utile come esempio di riferimento.
				</p>
			</v-card>
		</v-dialog>

		<!-- Modello su una mail gia' iniziata: la sostituisce, quindi si chiede -->
		<v-dialog v-model="modelDialog" max-width="420">
			<v-card v-if="pendingModel" class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 ma-0">Usare il modello «{{ pendingModel.label }}»?</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-4">
					Sostituisce i blocchi della mail. Se cambi idea, torni indietro con Annulla.
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="modelDialog = false">Lascia la mail com'è</v-btn>
					<v-btn text color="primary" @click="applyModel(pendingModel)">Usa il modello</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Unsaved changes dialog -->
		<v-dialog v-model="leaveDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 ma-0">Modifiche non salvate</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-4">
					Hai modifiche non salvate. Se esci adesso perderai tutto il lavoro fatto.
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="leaveDialog = false">Resta</v-btn>
					<v-btn text color="error" @click="confirmLeave">Esci senza salvare</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import HistoryManager from './builder/historyManager'
import { renderBlockContent, cloneBlockDeep, findBlockDef, hasTitleScale, createBlock, fitToColumns, legalGaps } from './builder/emailBlocks'
import { renderEmailToImage } from './builder/previewImage'
import { fillPreviewVariables } from './builder/previewVariables'
import { buildVocabulary, documentView, applyActions, historyContent } from './builder/aiContract'
import { generateImage, MAX_IMAGES_PER_REQUEST } from './builder/generateImage'
import { findModel, ADAPT_MODEL_PROMPT } from './builder/emailModels'

// Richiesta della seconda passata: non la scrive l'utente, e' il builder che rimanda la
// mail al modello insieme al render di com'e' venuta
var REVIEW_REQUEST = 'Guarda il render qui sopra: e\' la mail che hai appena composto, e ora la vedi per la prima volta. ' +
	'Confrontala con il modello se ce n\'e\' uno e correggi cio\' che stona di piu\': gerarchia e proporzioni dei testi, ' +
	'spazi, contrasto fra testo e sfondo (il logo su fondo scuro compreso), sezioni del modello che non hai riprodotto, ' +
	'soprattutto in coda alla mail. I testi sono gia\' quelli del cliente: correggi la forma, non riportarli a quelli ' +
	'del modello. Se e\' gia\' fedele non rimaneggiare: rispondi con actions vuoto e dillo.'

// Brand della mail all'apertura: quello salvato se esiste ancora, altrimenti il predefinito
// (stessa scelta del backend, Brand::resolve). null = il tenant non ha brand
function pickBrand(brands, savedId) {
	var chosen = brands.find(function(b) { return b.id === savedId }) ||
		brands.find(function(b) { return b.is_default }) || brands[0]
	return chosen ? chosen.id : null
}

import BlocksSidebar from './builder/BlocksSidebar'
import EditorCanvas from './builder/EditorCanvas'
import PropertiesPanel from './builder/PropertiesPanel'
import AiChat from './builder/AiChat'

export default {
	name: 'ai-builder',

	components: {
		'blocks-sidebar': BlocksSidebar,
		'editor-canvas': EditorCanvas,
		'properties-panel': PropertiesPanel,
		'ai-chat': AiChat
	},

	props: {
		id: { type: Number, required: true },
		modelDef: { type: String, default: 'templates-mailmarketing' },
		defaultInput: { type: Object, default: function() { return {} } }
	},

	// Il selettore immagini (annidato nel pannello) carica gli allegati sul template
	provide: function() {
		return { templateId: this.id }
	},

	data: function() {
		return {
			blocks: [],
			selectedBlockId: null,
			templateName: '',
			model: {},
			saving: false,
			loading: true,
			isDirty: false,
			showBorders: true,
			zoom: 100,
			zoomLevels: [
				{ text: '50%', value: 50 },
				{ text: '75%', value: 75 },
				{ text: '100%', value: 100 },
				{ text: '125%', value: 125 },
				{ text: '150%', value: 150 }
			],
			templateVariables: [],
			modelDialog: false,
			pendingModel: null,
			// Brand del tenant (dalla risposta di modifica del template) e quello della mail
			brands: [],
			brandId: null,
			previewDialog: false,
			previewScrollbar: 0,
			previewMode: 'desktop',
			exportDialog: false,
			exportMode: 'html',
			exportHtml: '',
			exportJson: '',
			aiView: null,
			rightTab: 'props',
			aiMessages: [],
			aiSuggestions: [],
			aiBusy: false,
			aiPhase: null,
			aiError: null,
			leaveDialog: false,
			leaveNext: null,
			history: null
		}
	},

	computed: {
		// La barra di scorrimento dell'anteprima ruba larghezza al documento: l'iframe si
		// allarga di altrettanto, cosi' alla mail restano esattamente i 600 (o 375) che
		// avrebbe nel client vero. Senza, tre celle da 186px non entravano nei 545 rimasti
		// e la griglia prodotti andava a capo 2+1 solo in anteprima
		previewWidth: function() {
			return (this.previewMode === 'mobile' ? 375 : 600) + this.previewScrollbar
		},

		selectedBlock: function() {
			if (!this.selectedBlockId) return null
			return this.findBlockById(this.blocks, this.selectedBlockId)
		},

		canUndo: function() {
			return this.history && this.history.canUndo()
		},

		// Dati legali che mancano al footer (vuoto = a posto o mail ancora vuota)
		footerGaps: function() {
			if (!this.blocks.length) return []
			var footer = this.footerBlock()
			return footer ? legalGaps(footer.settings) : ['il blocco Footer completo']
		},

		canRedo: function() {
			return this.history && this.history.canRedo()
		},

		exportText: function() {
			return this.exportMode === 'json' ? this.exportJson : this.exportHtml
		},

		selectedBlockLabel: function() {
			if (!this.selectedBlock) return ''
			var def = findBlockDef(this.selectedBlock.type)
			return def ? def.label : this.selectedBlock.type
		}
	},

	watch: {
		previewDialog: function(val) {
			if (val) {
				var self = this
				this.$nextTick(function() { self.renderPreview() })
			}
		}
	},

	created: function() {
		this.history = new HistoryManager(50)
		try {
			var saved = parseInt(localStorage.getItem('mm_builder_zoom'), 10)
			if (this.zoomLevels.some(function(l) { return l.value === saved })) this.zoom = saved
		} catch (e) { /* storage negato: si resta al 100% */ }
	},

	mounted: function() {
		this.loadTemplate()
		var self = this
		this._beforeUnload = function(e) {
			if (self.isDirty) {
				e.preventDefault()
				e.returnValue = ''
			}
		}
		window.addEventListener('beforeunload', this._beforeUnload)
	},

	beforeDestroy: function() {
		window.removeEventListener('beforeunload', this._beforeUnload)
	},

	beforeRouteLeave: function(to, from, next) {
		if (this.isDirty) {
			this.leaveNext = next
			this.leaveDialog = true
		} else {
			next()
		}
	},

	methods: {
		// --- Data loading / saving ---

		loadTemplate: function() {
			var self = this
			this.loading = true
			this.axios.get(this.modelDef + '/' + this.id + '/edit').then(function(response) {
				self.model = response.data.model
				self.templateName = self.model.name || ''
				self.brands = response.data.brands || []
				self.brandId = pickBrand(self.brands, self.model.brand_id)

				if (self.model.body) {
					try {
						var body = JSON.parse(self.model.body)
						if (body['builder-blocks']) {
							// Il content dei blocchi a settings si rigenera sempre: cosi' un template
							// salvato prende le migliorie del renderer alla prima apertura
							self.blocks = fitToColumns(self.rerenderAll(JSON.parse(body['builder-blocks'])))
						}
					} catch (e) {
						// Template vuoto o formato non riconosciuto
					}
				}

				// Carica variabili template dal BE
				var fields = response.data.fields && response.data.fields.fields
				if (fields) {
					for (var fi = 0; fi < fields.length; fi++) {
						if (fields[fi].type === 'email_variables' && fields[fi].variables) {
							self.templateVariables = fields[fi].variables
							break
						}
					}
				}

				self.loading = false
				self.history.push(self.blocks)
			}).catch(function() {
				self.loading = false
				self.$store.commit('setSnackbarError')
			})
		},

		save: function() {
			var self = this
			this.saving = true

			var html = this.renderBlocksToHtml()
			var css = 'p { margin: 0; }'

			var data = Object.assign({}, this.model, {
				brand_id: this.brandId,
				body: JSON.stringify({
					'gjs-html': html,
					'gjs-css': css,
					'gjs-components': '',
					'gjs-style': '',
					'builder-blocks': JSON.stringify(this.blocks)
				})
			})

			this.axios.put(this.modelDef + '/' + this.id, data).then(function() {
				self.saving = false
				self.isDirty = false
				self.$store.commit('setSnackbar', {
					timeout: 3000,
					text: 'Template salvato',
					value: true,
					color: 'success'
				})
			}).catch(function() {
				self.saving = false
				self.$store.commit('setSnackbarError')
			})
		},

		// --- Block operations ---

		selectBlock: function(id) {
			this.selectedBlockId = id
		},

		/**
		 * Input:  type del blocco scelto nella palette con un clic
		 * Fa:     lo inserisce SUBITO DOPO il blocco selezionato, allo stesso livello (dentro
		 *         la colonna, se e' li' che si sta lavorando); senza selezione va in fondo.
		 *         Il trascinamento resta, ma non e' piu' l'unico modo: dentro una colonna e'
		 *         un gesto di precisione, e chi provava a fare clic non otteneva niente
		 * Output: blocco aggiunto e selezionato
		 */
		addBlockFromPalette: function(type) {
			var nuovo = createBlock(type)
			var selezionato = this.selectedBlockId ? this.findBlockById(this.blocks, this.selectedBlockId) : null

			if (selezionato && selezionato.columns && !nuovo.columns) {
				// con un contenitore selezionato si riempie lui, non gli si mette qualcosa accanto:
				// la prima colonna vuota, o l'ultima se sono gia' tutte occupate
				var vuota = selezionato.columns.filter(function(c) { return !c.children.length })[0]
				var col = vuota || selezionato.columns[selezionato.columns.length - 1]
				col.children.push(nuovo)
			} else {
				var dove = selezionato ? this.findListOf(this.blocks, this.selectedBlockId) : null
				// un contenitore a colonne non puo' finire dentro una colonna
				if (dove && !(nuovo.columns && dove.nested)) {
					dove.list.splice(dove.index + 1, 0, nuovo)
				} else {
					this.blocks.push(nuovo)
				}
			}

			this.selectedBlockId = nuovo.id
			this.onBlocksChange()
		},

		/** Lista che contiene il blocco, la sua posizione, e se quella lista sta dentro una colonna */
		findListOf: function(blocks, id, nested) {
			for (var i = 0; i < blocks.length; i++) {
				if (blocks[i].id === id) return { list: blocks, index: i, nested: !!nested }
				if (blocks[i].columns) {
					for (var c = 0; c < blocks[i].columns.length; c++) {
						var found = this.findListOf(blocks[i].columns[c].children, id, true)
						if (found) return found
					}
				}
			}
			return null
		},

		footerBlock: function() {
			for (var i = this.blocks.length - 1; i >= 0; i--) {
				if (this.blocks[i].type === 'footer-full') return this.blocks[i]
			}
			return null
		},

		// Dal chip "Footer incompleto": porta al footer da completare, o lo aggiunge se non c'e'.
		// Un template vecchio chiude col "Footer Disiscrizione": il footer completo prende il suo
		// posto (ha anche la disiscrizione), accodato la mail ne avrebbe due. Annulla lo riporta
		selectFooter: function() {
			var footer = this.footerBlock()
			if (!footer) {
				footer = createBlock('footer-full')
				var legacy = this.blocks.findIndex(function(b) {
					var def = findBlockDef(b.type)
					return def && def.closing
				})
				if (legacy !== -1) {
					this.blocks.splice(legacy, 1, footer)
					this.$store.commit('setSnackbar', { timeout: 6000, text: 'Il vecchio footer è stato sostituito dal Footer completo', value: true, color: 'info' })
				} else {
					this.blocks.push(footer)
				}
				this.onBlocksChange()
			}
			this.selectedBlockId = footer.id
			this.rightTab = 'props'
		},

		askModel: function(key) {
			var model = findModel(key)
			if (!model) return
			// La risposta dell'assistente si innesterebbe nel modello: sezioni e footer doppi
			if (this.aiBusy) {
				this.$store.commit('setSnackbar', { timeout: 4000, text: 'Attendi la risposta dell\'assistente', value: true, color: 'warning' })
				return
			}
			if (!this.blocks.length) {
				this.applyModel(model)
				return
			}
			this.pendingModel = model
			this.modelDialog = true
		},

		/**
		 * Input:  modello di emailModels.js
		 * Fa:     mette i suoi blocchi al posto della mail (un passo di cronologia: Annulla lo
		 *         toglie) e propone in chat di adattarlo al brand con l'assistente
		 */
		applyModel: function(model) {
			this.modelDialog = false
			this.blocks = applyActions([], model.actions, {}).blocks
			this.selectedBlockId = null
			this.isDirty = true
			this.history.push(this.blocks)
			this.aiSuggestions = [ADAPT_MODEL_PROMPT]
			this.rightTab = 'chat'
		},

		onBlocksChange: function() {
			fitToColumns(this.blocks)
			this.isDirty = true
			this.history.push(this.blocks)
		},

		findBlockById: function(blocks, id) {
			for (var i = 0; i < blocks.length; i++) {
				if (blocks[i].id === id) return blocks[i]
				if (blocks[i].columns) {
					for (var c = 0; c < blocks[i].columns.length; c++) {
						var found = this.findBlockById(blocks[i].columns[c].children, id)
						if (found) return found
					}
				}
			}
			return null
		},

		onContentChange: function(payload) {
			var block = this.findBlockById(this.blocks, payload.id)
			if (block) block.content = payload.content
			this.isDirty = true
			this.history.push(this.blocks)
		},

		onStyleChange: function(payload) {
			var block = this.findBlockById(this.blocks, payload.id)
			if (block) {
				this.$set(block, 'styles', payload.styles)
				// I blocchi con render() leggono la tipografia dagli styles: rigenera
				block.content = renderBlockContent(block)
			}
			// padding e bordo di una sezione cambiano la larghezza delle sue colonne
			fitToColumns(this.blocks)
			this.isDirty = true
			this.history.push(this.blocks)
		},

		// Lo zoom resta per utente e per browser: chi lavora su un portatile lo tiene basso
		onZoomChange: function() {
			try { localStorage.setItem('mm_builder_zoom', String(this.zoom)) } catch (e) { /* quota piena o storage negato: lo zoom torna al default */ }
		},

		onTitleStyleChange: function(payload) {
			var block = this.findBlockById(this.blocks, payload.id)
			if (block) {
				this.$set(block, 'titleStyles', payload.titleStyles)
				block.content = renderBlockContent(block)
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		onSettingsChange: function(payload) {
			var block = this.findBlockById(this.blocks, payload.id)
			if (block) {
				this.$set(block, 'settings', payload.settings)
				block.content = this.rebuildBlockContent(block)
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		moveBlockUp: function(id) {
			for (var i = 1; i < this.blocks.length; i++) {
				if (this.blocks[i].id === id) {
					var temp = this.blocks[i]
					this.$set(this.blocks, i, this.blocks[i - 1])
					this.$set(this.blocks, i - 1, temp)
					break
				}
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		moveBlockDown: function(id) {
			for (var i = 0; i < this.blocks.length - 1; i++) {
				if (this.blocks[i].id === id) {
					var temp = this.blocks[i]
					this.$set(this.blocks, i, this.blocks[i + 1])
					this.$set(this.blocks, i + 1, temp)
					break
				}
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		duplicateBlock: function(id) {
			for (var i = 0; i < this.blocks.length; i++) {
				if (this.blocks[i].id === id) {
					this.blocks.splice(i + 1, 0, cloneBlockDeep(this.blocks[i]))
					break
				}
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		deleteBlock: function(id) {
			for (var i = 0; i < this.blocks.length; i++) {
				if (this.blocks[i].id === id) {
					this.blocks.splice(i, 1)
					if (this.selectedBlockId === id) this.selectedBlockId = null
					break
				}
			}
			this.isDirty = true
			this.history.push(this.blocks)
		},

		// --- Undo / Redo ---

		undo: function() {
			var state = this.history.undo()
			if (state) {
				this.blocks = state
				this.isDirty = true
			}
		},

		redo: function() {
			var state = this.history.redo()
			if (state) {
				this.blocks = state
				this.isDirty = true
			}
		},

		rebuildBlockContent: function(block) {
			return renderBlockContent(block)
		},

		rerenderAll: function(blocks) {
			var self = this
			return (blocks || []).map(function(b) {
				if (b.columns) {
					b.columns.forEach(function(col) { col.children = self.rerenderAll(col.children) })
				} else {
					// I template salvati prima della seconda scala tipografica non hanno titleStyles:
					// senza questo, su di essi il titolo resterebbe non modificabile
					if (!b.titleStyles && hasTitleScale(findBlockDef(b.type))) b.titleStyles = {}
					b.content = renderBlockContent(b)
				}
				return b
			})
		},

		// --- HTML rendering ---

		renderBlockToHtml: function(block) {
			var styleStr = this.styleObjToString(block.styles || {})

			// Layout block with columns
			if (block.columns) {
				var cells = ''
				for (var c = 0; c < block.columns.length; c++) {
					var col = block.columns[c]
					var childrenHtml = ''
					for (var ch = 0; ch < col.children.length; ch++) {
						childrenHtml += this.renderBlockToHtml(col.children[ch])
					}
					if (!childrenHtml) childrenHtml = '&nbsp;'
					cells += '<td width="' + col.width + '" style="vertical-align:top;padding:0;">' + childrenHtml + '</td>'
				}
				return '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;' + styleStr + '"><tr>' + cells + '</tr></table>'
			}

			// Content block (data-field serve solo all'editing nel canvas: via dall'HTML inviato)
			var content = (block.content || '').replace(/ data-field="[^"]*"/g, '')
			return '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr><td style="' + styleStr + '">' + content + '</td></tr></table>'
		},

		renderBlocksToHtml: function() {
			if (!this.blocks.length) return ''

			var rows = ''
			for (var i = 0; i < this.blocks.length; i++) {
				rows += '<tr><td style="padding:0;">' + this.renderBlockToHtml(this.blocks[i]) + '</td></tr>'
			}

			return '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">' +
				'<tr><td align="center" style="padding:0;">' +
				'<table width="600" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="border-collapse:collapse;max-width:600px;width:100%;background-color:#ffffff;">' +
				rows +
				'</table>' +
				'</td></tr></table>'
		},

		styleObjToString: function(obj) {
			var parts = []
			for (var key in obj) {
				if (obj[key]) parts.push(key + ':' + obj[key])
			}
			return parts.join(';')
		},

		// --- Preview ---

		renderPreview: function(attempt) {
			var iframe = this.$refs.previewFrame
			if (!iframe || !(iframe.contentDocument || iframe.contentWindow)) {
				// Il dialog puo' montare l'iframe un tick dopo l'apertura: riprova, non tacere
				var self = this
				if ((attempt || 0) < 10) setTimeout(function() { self.renderPreview((attempt || 0) + 1) }, 50)
				return
			}
			// Solo qui le variabili si riempiono: nel template e nell'HTML esportato restano
			// {{NOME}}, e le sostituisce il backend all'invio col destinatario vero
			var html = fillPreviewVariables(this.renderBlocksToHtml())
			var doc = iframe.contentDocument || iframe.contentWindow.document
			doc.open()
			doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:0;background:#fff;}p{margin:0;}</style></head><body>' + html + '</body></html>')
			doc.close()
			this.measurePreviewScrollbar()
		},

		/** Quanta larghezza si prende la barra di scorrimento dell'anteprima (0 se non c'e') */
		measurePreviewScrollbar: function() {
			var self = this
			this.$nextTick(function() {
				var iframe = self.$refs.previewFrame
				var doc = iframe && iframe.contentDocument
				if (!doc || !doc.documentElement) return
				var barra = iframe.contentWindow.innerWidth - doc.documentElement.clientWidth
				self.previewScrollbar = barra > 0 && barra < 40 ? barra : 0
			})
		},

		// --- Export ---

		openExportDialog: function() {
			var raw = this.renderBlocksToHtml()
			this.exportHtml = this.formatHtml(raw)
			this.exportJson = JSON.stringify(documentView(this.blocks).doc, null, 2)
			this.exportDialog = true
		},

		formatHtml: function(html) {
			var indent = 0
			var result = ''
			// Splitta per tag
			var tokens = html.replace(/>\s*</g, '>\n<').split('\n')
			for (var i = 0; i < tokens.length; i++) {
				var line = tokens[i].trim()
				if (!line) continue

				// Tag di chiusura: riduci indent prima
				if (line.match(/^<\//) && indent > 0) {
					indent--
				}

				var pad = ''
				for (var j = 0; j < indent; j++) pad += '  '
				result += pad + line + '\n'

				// Tag di apertura (non self-closing, non chiusura): aumenta indent
				if (line.match(/^<[a-z]/) && !line.match(/\/>$/) && !line.match(/^<(br|hr|img|input|meta|link)/i)) {
					// Non aumentare se il tag si chiude sulla stessa riga
					var tagName = line.match(/^<([a-z]+)/i)
					if (tagName && line.indexOf('</' + tagName[1]) === -1) {
						indent++
					}
				}
			}
			return result.trim()
		},

		copyExportHtml: function() {
			var self = this
			if (navigator.clipboard) {
				navigator.clipboard.writeText(this.exportText).then(function() {
					self.$store.commit('setSnackbar', {
						timeout: 2000,
						text: 'HTML copiato negli appunti',
						value: true,
						color: 'success'
					})
				})
			} else {
				var ta = this.$refs.exportTextarea
				if (ta) {
					ta.select()
					document.execCommand('copy')
					this.$store.commit('setSnackbar', {
						timeout: 2000,
						text: 'HTML copiato negli appunti',
						value: true,
						color: 'success'
					})
				}
			}
		},

		// --- Assistente AI (contratto in builder/aiContract.js) ---

		/**
		 * Input:  message — richiesta dell'utente
		 * Fa:     costruisce il payload per il backend e ricorda la vista inviata (alias → id)
		 * Output: { message, selected, doc, vocabulary }
		 */
		buildAiRequest: function(message) {
			this.aiView = documentView(this.blocks)
			var selected = null
			for (var alias in this.aiView.aliasToId) {
				if (this.aiView.aliasToId[alias] === this.selectedBlockId) selected = alias
			}
			return {
				message: message,
				selected: selected,
				doc: this.aiView.doc,
				vocabulary: buildVocabulary(),
				// quello scelto adesso, anche se la mail non e' ancora salvata
				brand_id: this.brandId
			}
		},

		/**
		 * Input:  actions — operazioni emesse dal modello per l'ultima vista inviata
		 * Fa:     le applica (quelle non valide vengono scartate una per una) e registra un passo di cronologia
		 * Output: { applied, rejected, notes, images }
		 */
		applyAiActions: function(actions) {
			var result = applyActions(this.blocks, actions, this.aiView ? this.aiView.aliasToId : {})
			if (result.applied) {
				this.blocks = result.blocks
				this.isDirty = true
				this.history.push(this.blocks)
			}
			return { applied: result.applied, rejected: result.rejected, notes: result.notes, images: result.images }
		},

		/**
		 * Input:  msg (il messaggio di chat appena scritto), pending (foto chieste dal modello)
		 * Fa:     le genera in parallelo, fino al tetto per richiesta, e le infila nei blocchi
		 *         appena arrivano. In parallelo e non dentro la richiesta AI perche' cosi' la
		 *         mail si compone subito e le foto si riempiono dopo, invece di far aspettare
		 *         quattro secondi per immagine prima di vedere qualsiasi cosa.
		 *         Quella che fallisce lascia il segnaposto col suo prompt: si rigenera a mano
		 *         dal pannello, senza aver perso il resto
		 * Output: aggiorna blocchi e contatori del messaggio
		 */
		generateAiImages: function(msg, pending) {
			var self = this

			// Un'operazione respinta dopo aver messo in coda la sua foto lascerebbe un id
			// fantasma: si tengono solo i blocchi che sono davvero finiti nel documento
			var list = (pending || []).filter(function(item) {
				return !!self.findBlockById(self.blocks, item.blockId)
			}).slice(0, MAX_IMAGES_PER_REQUEST)

			if (!list.length) return Promise.resolve()
			msg.imagesTotal = list.length

			return Promise.all(list.map(function(item) {
				return generateImage(self.axios, self.$store.getters.baseUrl, {
					prompt: item.prompt,
					templateId: self.id,
					ratio: item.ratio
				}).then(function(result) {
					if (!result.url) throw new Error('nessuna immagine')
					var block = self.findBlockById(self.blocks, item.blockId)
					if (!block) throw new Error('blocco sparito')
					self.$set(block.settings, item.srcKey, result.url)
					block.content = self.rebuildBlockContent(block)
					self.isDirty = true
					msg.imagesDone++
				}).catch(function() {
					msg.imagesFailed++
				})
			}))
		},

		/**
		 * Input:  reference — il modello portato dall'utente, se c'era
		 * Fa:     rimanda la mail al modello con il render di com'e' venuta e gli chiede di
		 *         correggersi. Senza questo giro il render non serve a chi compone: quello
		 *         allegato a una richiesta e' sempre lo stato PRECEDENTE, quindi chi scrive
		 *         "fammi una mail cosi'" riceve un lavoro fatto a occhi chiusi che nessuno
		 *         rilegge. Parte solo a foto generate dentro, altrimenti il modello vedrebbe
		 *         i riquadri grigi e proverebbe a "correggerli"
		 * Output: un secondo messaggio dell'assistente in chat
		 */
		runAutoReview: function(reference) {
			var self = this
			var payload = this.buildAiRequest(REVIEW_REQUEST)
			payload.template_id = this.id
			payload.variables = this.templateVariables.map(function(v) { return String(v.value || v.text || v) })
			payload.history = this.aiMessages.slice(-6).map(function(m) {
				return { role: m.role, content: historyContent(m) }
			})

			this.aiBusy = true
			this.aiPhase = 'review'

			renderEmailToImage(fillPreviewVariables(this.renderBlocksToHtml(), true)).then(function(render) {
				// senza render non c'e' niente da rileggere: si lascia la mail com'e'
				if (!render) { self.aiBusy = false; self.aiPhase = null; return }
				payload.images = []
				if (reference) payload.images.push(Object.assign({ role: 'reference' }, reference))
				payload.images.push(Object.assign({ role: 'render' }, render))
				self.postAiRequest(payload, { isReview: true })
			})
		},

		openAiForBlock: function(id) {
			this.selectedBlockId = id
			this.rightTab = 'chat'
		},

		clearAiChat: function() {
			this.aiMessages = []
			this.aiSuggestions = []
			this.aiError = null
		},

		/**
		 * Input:  text — messaggio dell'utente; image — {media_type, data} opzionale (screenshot allegato)
		 * Fa:     manda al backend richiesta + vista + vocabolario + ultimi 3 scambi (+ immagine se presente),
		 *         applica le operazioni ricevute e registra la risposta in chat
		 * Output: aggiorna aiMessages / aiSuggestions / aiError
		 */
		sendAiMessage: function(text, image) {
			var self = this
			this.aiError = null
			this.aiSuggestions = []
			this.aiMessages.push({ role: 'user', content: text, image: image || null })

			// Merita una riletta chi compone da zero o chi porta un modello da riprodurre: e' li'
			// che il modello lavora senza aver mai visto il risultato. Su una modifica puntuale
			// ("cambia il colore del bottone") il render c'e' gia' e il secondo giro sarebbe sprecato
			var review = (!this.blocks.length || !!image) ? { reference: image || null } : null
			this.aiPhase = 'compose'

			var payload = this.buildAiRequest(text)
			payload.template_id = this.id
			payload.variables = this.templateVariables.map(function(v) { return String(v.value || v.text || v) })
			payload.history = this.aiMessages.slice(-7, -1).map(function(m) {
				return { role: m.role, content: historyContent(m) }
			})

			this.aiBusy = true

			// Il render di com'e' adesso la mail va insieme alla richiesta: senza, l'assistente
			// compone alla cieca e non sa mai come e' venuto quello che ha appena fatto.
			// Si parte dall'HTML dell'anteprima, non dal canvas: nell'editor ci sono bordi
			// tratteggiati e segnaposto che nella mail vera non esistono, e il modello
			// proverebbe a "correggerli". Se la cattura fallisce si procede senza.
			renderEmailToImage(this.blocks.length ? fillPreviewVariables(this.renderBlocksToHtml(), true) : '').then(function(render) {
				payload.images = []
				if (image) payload.images.push(Object.assign({ role: 'reference' }, image))
				if (render) payload.images.push(Object.assign({ role: 'render' }, render))
				self.postAiRequest(payload, { image: image, review: review })
			})
		},

		postAiRequest: function(payload, opts) {
			var image = opts && opts.image
			var review = opts && opts.review
			var self = this
			this.axios.post('ai/mail-marketing/builder', payload).then(function(response) {
				var data = response.data || {}
				var outcome = self.applyAiActions(data.actions || [])
				var msg = {
					role: 'assistant',
					content: data.message || (outcome.applied ? 'Fatto.' : 'Non ho trovato modifiche da fare.'),
					applied: outcome.applied,
					rejected: outcome.rejected,
					notes: outcome.notes,
					usage: data.usage || null,
					imagesTotal: 0,
					imagesDone: 0,
					imagesFailed: 0,
					review: !!(opts && opts.isReview)
				}
				self.aiMessages.push(msg)
				self.aiSuggestions = Array.isArray(data.suggestions) ? data.suggestions.slice(0, 3) : []
				self.aiBusy = false
				self.aiPhase = null
				// dopo aiBusy: la mail e' gia' a schermo, le foto si riempiono da sole
				self.generateAiImages(msg, outcome.images).then(function() {
					if (review && outcome.applied) self.runAutoReview(review.reference)
				})
			}).catch(function(error) {
				var status = error.response && error.response.status
				var data = (error.response && error.response.data) || {}

				// JSON malformato: una graffa fuori posto butterebbe via la mail appena composta.
				// Si rimanda la stessa identica richiesta, una volta sola
				if (data.retriable && !(opts && opts.retried)) {
					self.postAiRequest(payload, Object.assign({}, opts, { retried: true }))
					return
				}

				self.aiBusy = false
				self.aiPhase = null
				// la richiesta non è mai arrivata: la chat rimette l'allegato nel chip
				if (image && self.$refs.aiChat) self.$refs.aiChat.restoreImage()
				var serverMsg = data.message
				self.aiError = serverMsg ||
					(status === 429 ? 'Hai raggiunto il limite di richieste: riprova tra poco.' :
						status === 503 ? 'L\'assistente non è configurato su questo ambiente.' :
							'L\'assistente non ha risposto. Riprova.')
			})
		},

		// --- Navigation ---

		goBack: function() {
			if (this.isDirty) {
				var self = this
				this.leaveNext = function() {
					self.$router.push({
						name: 'templates-mailmarketing-show',
						params: { id: self.id }
					})
				}
				this.leaveDialog = true
			} else {
				this.$router.push({
					name: 'templates-mailmarketing-show',
					params: { id: this.id }
				})
			}
		},

		confirmLeave: function() {
			this.leaveDialog = false
			this.isDirty = false
			if (typeof this.leaveNext === 'function') {
				this.leaveNext()
			}
		}
	}
}
</script>

<style scoped>
.ai-builder {
	position: fixed;
	top: 65px;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	z-index: 7;
	background: #fff;
	/* stesso passo del menu laterale quando si allarga o si stringe */
	transition: left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-builder__toolbar {
	z-index: 3;
	border-bottom: 1px solid #e0e0e0;
	flex: 0 0 48px !important;
	max-height: 48px !important;
}

.ai-builder__brand {
	width: 220px;
}

/* Icona dentro il campo: senza etichetta Vuetify la lascia in alto */
.ai-builder__brand ::v-deep .v-input__prepend-inner {
	align-self: center;
	margin-top: 0 !important;
}

.ai-builder__brand-swatch {
	flex: 0 0 auto;
	width: 12px;
	height: 12px;
	margin-right: 8px;
	border-radius: 50%;
	box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}

.ai-builder__content {
	display: flex;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.ai-builder__sidebar-left {
	width: 30px;
	min-width: 290px;
	border-right: 1px solid #e0e0e0;
	overflow-y: auto;
}

.ai-builder__sidebar-right {
	width: 360px;
	min-width: 360px;
	border-left: 1px solid #e0e0e0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	background: #fff;
}

.ai-builder__tabs {
	display: flex;
	border-bottom: 1px solid #e0e0e0;
	flex: 0 0 auto;
}

.ai-builder__tab {
	flex: 1;
	height: 42px;
	border: 0;
	border-bottom: 2px solid transparent;
	background: #fafafa;
	font-size: 13px;
	font-weight: 500;
	color: #616161;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
}

.ai-builder__tab .v-icon {
	color: inherit !important;
}

.ai-builder__tab--active {
	background: #fff;
	color: var(--v-primary-base, #1976d2);
	border-bottom-color: var(--v-primary-base, #1976d2);
}

.ai-builder__sidebar-body {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
}

.ai-builder__preview-stage {
	display: flex;
	justify-content: center;
	background: #e0e0e0;
	overflow: hidden;
}

.ai-builder__preview-frame {
	height: 100%;
	max-width: 100%;
	border: none;
	background: #fff;
	transition: width 0.2s;
}

.ai-builder__export-code {
	width: 100%;
	min-height: 60vh;
	padding: 16px;
	font-family: 'Courier New', Courier, monospace;
	font-size: 12px;
	line-height: 1.5;
	border: none;
	outline: none;
	resize: none;
	background: #fafafa;
	color: #333;
	tab-size: 2;
}
</style>
